import { useState } from 'react';
import { useRunnerNavigationCounts } from './lib/useRunnerNavigationCounts';
import { useRunnerNotices } from './lib/useRunnerNotices';
import { RunnerMessage } from './components/RunnerMessage';
import { ContinueOnPhone } from './components/ContinueOnPhone';
import { prettyRunDate, prettyTime } from './lib/runs';
import { Routes, Route, Navigate, Link } from 'react-router';
import { Alert, Box, Spinner, Typography } from '@glplus/glpweb-dsl';
import { RequireAuth } from './components/RequireAuth';
import { AppShell } from './components/AppShell';
import { useRunner } from './lib/useRunner';
import Login from './routes/Login';
import RunnerLegalDocument from './routes/RunnerLegalDocument';
import AccountRecovery from './routes/AccountRecovery';
import AccountDeletion, { AccountClosed } from './routes/AccountDeletion';
import ApplicationStatus from './routes/ApplicationStatus';
import Dashboard from './routes/Dashboard';
import Runs from './routes/Runs';
import History from './routes/History';
import OrderAttention from './routes/OrderAttention';
import OpenRuns from './routes/OpenRuns';
import Schedule from './routes/Schedule';
import Record from './routes/Record';
import Banking from './routes/Banking';
import Earnings from './routes/Earnings';
import Customers from './routes/Customers';
import Account from './routes/Account';
import AccountHub from './routes/AccountHub';
import PrivacyNotifications from './routes/PrivacyNotifications';
import PhotoAndVehicle from './routes/PhotoAndVehicle';
import Kit from './routes/Kit';
import KitCategory from './routes/KitCategory';
import KitGuide from './routes/KitGuide';
import KitTools from './routes/KitTools';
import Handbook from './routes/Handbook';
import Help from './routes/Help';
import Insurance from './routes/Insurance';
import Messages, { MessageDetail } from './routes/Messages';
import { useTranslation } from 'react-i18next';

/**
 * Runner portal — runner.grocerylistplus.com. ISOLATED by ruling (2026-08-24):
 * Runner Cognito pool, runner-api only, DSL the only shared package.
 * /login is outside the guard. Inside it, the nav is built from what the
 * signed-in account can actually do today: applicants see Application +
 * Account; active Runners see Dashboard + Account.
 */
export default function App() {
  return (
    <Routes>
      <Route path="/legal/terms" element={<RunnerLegalDocument document="terms" />} />
      <Route path="/legal/privacy" element={<RunnerLegalDocument document="privacy" />} />
      <Route path="/login" element={<Login />} />
      <Route path="/account/closed" element={<AccountClosed />} />
      <Route path="/account/restore" element={<AccountRecovery />} />
      <Route
        path="/*"
        element={
          <RequireAuth>
            <Portal />
          </RequireAuth>
        }
      />
    </Routes>
  );
}

function Portal() {
  const { t, i18n } = useTranslation();
  const { me, application, loading, error, reload } = useRunner();
  const { activeRun, disputeCount } = useRunnerNotices(me);
  const navigationCounts = useRunnerNavigationCounts(me);
  const [phoneRun, setPhoneRun] = useState<string | null>(null);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 12 }}>
        <Spinner aria-label={t('app.loading')} data-testid="app-loading" />
      </Box>
    );
  }
  if (error || !me) {
    return (
      <AppShell nav={[]}>
        <Alert severity="error" data-testid="app-load-error">{error ?? t('app.loadFailed')}</Alert>
      </AppShell>
    );
  }

  if (me.recovery) return <AccountRecovery recovery={me.recovery} onRestored={reload} />;
  const applicant = me.lifecycle === 'applicant';
  const nav = applicant
    ? [{ label: t('nav.application'), to: '/application' }, { label: t('nav.photo'), to: '/photo' }, { label: t('nav.banking'), to: '/banking' }]
    : [{ label: t('nav.dashboard'), to: '/' }, { label: t('nav.runs'), to: '/runs' }, { label: t('nav.openRuns'), to: '/open-runs' }, { label: t('nav.schedule'), to: '/schedule' }, { label: t('nav.record'), to: '/record' }, { label: t('runnerCustomers.mineTitle'), to: '/customers' }, { label: t('runnerHome.potentialTitle'), to: '/potential-customers' }, { label: t('nav.earnings'), to: '/earnings' }, { label: t('nav.photo'), to: '/photo' }, { label: t('nav.banking'), to: '/banking' }];
  const displayName = me.displayName ?? (application ? `${application.firstName}` : null);

  const es = i18n.language.startsWith('es');
  const locale = es ? 'es-US' : 'en-US';
  const notices = <>
    {['suspended', 'paused'].includes(me.lifecycle) ? <RunnerMessage title={es ? 'Las entregas están pausadas' : 'Deliveries are paused'} body={es ? 'No puedes iniciar nuevas entregas. Puedes consultar tus ganancias e historial y contactar a soporte.' : 'You cannot start new deliveries. Earnings, history and support remain available.'} action={es ? 'Contactar a soporte' : 'Contact support'} href="/help" /> : null}
    {activeRun ? <RunnerMessage title={t('runnerHome.continueTitle')} body={`${prettyRunDate(activeRun.runDate, locale)} · ${prettyTime(activeRun.deliveryWindowStart, locale)}–${prettyTime(activeRun.deliveryWindowEnd, locale)}\n${t('approvedHome.orders', { count: activeRun.orderCount })}`} action={me.lifecycle === 'active' ? t('runnerHome.continueRun') : (es ? 'Ver registro' : 'View record')} href={me.lifecycle === 'active' ? undefined : `/runs?run=${activeRun.id}`} onAction={me.lifecycle === 'active' ? () => setPhoneRun(activeRun.id) : undefined} /> : null}
    {disputeCount && disputeCount > 0 ? <RunnerMessage title={es ? 'Una entrega necesita tu respuesta' : 'A delivery needs your response'} body={es ? 'Revisa los problemas reportados y contacta a soporte.' : 'Review reported issues and contact support.'} action={es ? 'Ver disputas' : 'View disputes'} href="/disputes" /> : null}
  </>;
  return (
    <AppShell nav={nav} displayName={displayName} email={me.email} showInbox={!applicant} topNotice={notices} navigationCounts={navigationCounts}>
      <ContinueOnPhone runId={phoneRun} onClose={() => setPhoneRun(null)} />
      {!applicant && me.phoneRequired && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="warning" data-testid="phone-required-banner">
            {t('phone.banner')}{' '}
            <Typography component={Link} to="/account" data-testid="phone-required-link" sx={{ fontWeight: 700, color: 'inherit', fontSize: 'inherit' }}>
              {t('phone.bannerLink')}
            </Typography>
          </Alert>
        </Box>
      )}
      {!applicant && me.handbookAckRequired && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="warning" data-testid="handbook-reack-banner">
            {t('handbook.reackBanner')}{' '}
            <Typography component={Link} to="/handbook" data-testid="handbook-reack-link" sx={{ fontWeight: 700, color: 'inherit', fontSize: 'inherit' }}>
              {t('handbook.reackLink')}
            </Typography>
          </Alert>
        </Box>
      )}
      {!applicant && me.insuranceRequired && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="warning" data-testid="insurance-required-banner">
            {t('insurance.banner')}{' '}
            <Typography component={Link} to="/insurance" data-testid="insurance-required-link" sx={{ fontWeight: 700, color: 'inherit', fontSize: 'inherit' }}>
              {t('insurance.bannerLink')}
            </Typography>
          </Alert>
        </Box>
      )}
      <Routes>
        <Route path="/" element={applicant ? <Navigate to="/application" replace /> : <Dashboard me={me} />} />
        <Route path="/application" element={<ApplicationStatus me={me} application={application} reload={reload} />} />
        <Route path="/privacy" element={<PrivacyNotifications canEdit={me.capabilities.includes('runner:profile:write')} />} />
        <Route path="/account/overview" element={<AccountHub />} />
        <Route path="/account" element={<Account me={me} application={application} reload={reload} />} />
        <Route path="/account/delete" element={<AccountDeletion />} />
        <Route path="/photo" element={<PhotoAndVehicle me={me} application={application} />} />
        <Route path="/runs" element={<Runs canContinue={me.capabilities.includes('runner:runs:field')} />} />
        <Route path="/history" element={<History />} />
        <Route path="/pending-approval" element={<OrderAttention kind="pending" />} />
        <Route path="/disputes" element={<OrderAttention kind="disputes" />} />
        <Route path="/open-runs" element={<OpenRuns />} />
        <Route path="/schedule" element={<Schedule />} />
        <Route path="/record" element={<Record />} />
        <Route path="/banking" element={<Banking canReadEarnings={me.capabilities.includes('runner:earnings:read')} />} />
        <Route path="/earnings" element={<Earnings />} />
        <Route path="/customers" element={<Customers view="mine" />} />
        <Route path="/potential-customers" element={<Customers view="potential" />} />
        <Route path="/help" element={<Help />} />
        <Route path="/kit" element={<Kit />} />
        <Route path="/kit/tools" element={<KitTools />} />
        <Route path="/kit/:category" element={<KitCategory />} />
        <Route path="/kit/:category/:guide" element={<KitGuide />} />
        <Route path="/insurance" element={<Insurance onChanged={reload} />} />
        <Route path="/handbook" element={<Handbook application={application} reload={reload} />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/messages/:id" element={<MessageDetail />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}
